import { Injectable, signal, computed, effect } from '@angular/core';
import { StepperStepData, StepperStepConfig } from './stepper.types';

/**
 * StepperService
 * 
 * Scoped service for managing stepper state and data sharing between steps.
 * Each stepper instance gets its own service instance (provided at component level).
 * 
 * This follows the pattern used by Angular Material Stepper and similar libraries.
 * 
 * @example
 * ```typescript
 * // In stepper component:
 * providers: [StepperService]
 * 
 * // In step components:
 * constructor(private stepperService: StepperService) {
 *   // Access shared data
 *   const projectData = this.stepperService.getData('project');
 *   
 *   // Set step data
 *   this.stepperService.setStepData('create-project', { name: 'My Project' });
 * }
 * ```
 */
@Injectable()
export class StepperService {
  /** Internal storage for step data */
  private readonly stepData = signal<StepperStepData>({});

  /** Current step index */
  readonly currentStepIndex = signal<number>(0);

  /** Step configurations */
  readonly steps = signal<StepperStepConfig[]>([]);

  /** Computed: Current step configuration */
  readonly currentStep = computed(() => {
    const index = this.currentStepIndex();
    const steps = this.steps();
    return steps[index] || null;
  });

  /** Computed: Total steps */
  readonly totalSteps = computed(() => this.steps().length);

  /** Computed: Remaining steps count */
  readonly remainingSteps = computed(() => {
    return Math.max(0, this.totalSteps() - this.currentStepIndex());
  });

  /** Computed: Is first step */
  readonly isFirstStep = computed(() => this.currentStepIndex() === 0);

  /** Computed: Is last step */
  readonly isLastStep = computed(() => {
    return this.currentStepIndex() === this.totalSteps() - 1;
  });

  /** Computed: Progress percentage (0-100) */
  readonly progress = computed(() => {
    const total = this.totalSteps();
    if (total === 0) return 0;
    return ((this.currentStepIndex() + 1) / total) * 100;
  });

  constructor() {
    // Reset data when steps change
    effect(() => {
      this.steps(); // Track changes
      // Optionally reset data when steps change
    });
  }

  /**
   * Set data for a specific step
   */
  setStepData(stepId: string, data: any): void {
    this.stepData.update(current => ({
      ...current,
      [stepId]: data
    }));
  }

  /**
   * Get data for a specific step
   */
  getStepData<T = any>(stepId: string): T | undefined {
    return this.stepData()[stepId] as T | undefined;
  }

  /**
   * Get all stepper data
   */
  getAllData(): StepperStepData {
    return this.stepData();
  }

  /**
   * Update data for a specific step (merge with existing)
   */
  updateStepData(stepId: string, data: Partial<any>): void {
    const current = this.getStepData(stepId) || {};
    this.setStepData(stepId, { ...current, ...data });
  }

  /**
   * Clear data for a specific step
   */
  clearStepData(stepId: string): void {
    this.stepData.update(current => {
      const updated = { ...current };
      delete updated[stepId];
      return updated;
    });
  }

  /**
   * Clear all stepper data
   */
  clearAllData(): void {
    this.stepData.set({});
  }

  /**
   * Set current step index
   */
  setCurrentStep(index: number): void {
    const maxIndex = this.totalSteps() - 1;
    if (index >= 0 && index <= maxIndex) {
      this.currentStepIndex.set(index);
    }
  }

  /**
   * Go to next step
   */
  nextStep(): void {
    if (!this.isLastStep()) {
      this.currentStepIndex.update(idx => idx + 1);
    }
  }

  /**
   * Go to previous step
   */
  previousStep(): void {
    if (!this.isFirstStep()) {
      this.currentStepIndex.update(idx => idx - 1);
    }
  }

  /**
   * Go to specific step
   */
  goToStep(index: number): void {
    this.setCurrentStep(index);
  }

  /**
   * Go to step by ID
   */
  goToStepById(stepId: string): void {
    const index = this.steps().findIndex(step => step.id === stepId);
    if (index !== -1) {
      this.setCurrentStep(index);
    }
  }

  /**
   * Reset stepper to initial state
   */
  reset(): void {
    this.currentStepIndex.set(0);
    this.clearAllData();
  }
}

